import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import TemplateRaciAssignments from '../assignments/TemplateRaciAssignments';
import { toast } from 'react-hot-toast';

// Simple error boundary for RACI components
class RaciErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    
    // Add identifier for this instance
    this.instanceId = Math.random().toString(36).substring(2, 8);
    console.log(`[RaciErrorBoundary-${this.instanceId}] Created new instance`);
  }

  static getDerivedStateFromError(error) {
    // This is called when a child throws an error
    console.error(`[RaciErrorBoundary] getDerivedStateFromError called with:`, error);
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log detailed error information
    console.error(`[RaciErrorBoundary-${this.instanceId}] Error caught:`, error);
    console.error(`[RaciErrorBoundary-${this.instanceId}] Component stack:`, errorInfo.componentStack);
    this.setState({ errorInfo });
  }

  render() {
    // Log render cycle for debugging
    console.log(`[RaciErrorBoundary-${this.instanceId}] Rendering, hasError: ${this.state.hasError}`);
    
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded">
          <p className="text-red-600">Error loading assignments. Please try refreshing the page.</p>
          <details className="mt-2 text-xs text-red-800">
            <summary>Error details (for developers)</summary>
            <p className="mt-1">{this.state.error && this.state.error.toString()}</p>
            {this.state.errorInfo && <pre className="mt-1 bg-red-100 p-2 rounded overflow-auto">{this.state.errorInfo.componentStack}</pre>}
          </details>
        </div>
      );
    }
    
    return this.props.children;
  }
}

// A safer approach to rendering RACI assignments components
const SafeRaciContainer = ({ entityType, entityId }) => {
  // Debug logging for props received
  console.log(`[SafeRaciContainer] Rendering for ${entityType}:${entityId}, props:`, { entityType, entityId });
  
  // Ensure both entityType and entityId are provided
  if (!entityType || !entityId) {
    console.warn(`[SafeRaciContainer] Missing required props: entityType=${entityType}, entityId=${entityId}`);
    return (
      <div className="bg-white shadow rounded-lg p-4">
        <p className="text-gray-500">RACI assignments will be available after saving.</p>
      </div>
    );
  }
  
  console.log(`[SafeRaciContainer] Rendering TemplateRaciAssignments for ${entityType}:${entityId}`);
  
  try {
    // Wrap in try/catch for additional debugging
    return <TemplateRaciAssignments entityType={entityType} entityId={entityId} />;
  } catch (error) {
    console.error(`[SafeRaciContainer] Error rendering TemplateRaciAssignments:`, error);
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded">
        <p className="text-red-600">Error rendering assignments component. See console for details.</p>
      </div>
    );
  }
};

const TemplateForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    phases: []
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPhase, setCurrentPhase] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);
  
  // Fetch template data if editing
  useEffect(() => {
    if (isEditing) {
      console.log(`[TemplateForm] Starting to fetch template with ID: ${id}`);
      
      const fetchTemplate = async () => {
        try {
          setLoading(true);
          setError(null);
          
          // Don't try to fetch if id is "add" - this is for creating a new template
          if (id === "add" || id === undefined) {
            console.log(`[TemplateForm] ID is "add" or undefined, not fetching`);
            setLoading(false);
            return;
          }
          
          console.log(`[TemplateForm] Fetching template data from API for ID: ${id}`);
          const response = await api.get(`/projects/templates/${id}`);
          const templateData = response.data;
          
          console.log('[TemplateForm] Received template data:', templateData);
          
          // Debug check for template data integrity
          if (!templateData || !templateData.id) {
            console.warn('[TemplateForm] Template data seems incomplete:', templateData);
          }
          
          // Log phase and step data for troubleshooting
          if (templateData.phases) {
            console.log(`[TemplateForm] Template has ${templateData.phases.length} phases`);
            templateData.phases.forEach((phase, i) => {
              console.log(`[TemplateForm] Phase ${i+1}: ID=${phase.id}, Name=${phase.name}, Steps=${phase.steps?.length || 0}`);
              if (phase.steps) {
                phase.steps.forEach((step, j) => {
                  console.log(`[TemplateForm] - Step ${j+1}: ID=${step.id}, Name=${step.name}`);
                });
              }
            });
          } else {
            console.warn('[TemplateForm] Template has no phases array');
          }
          
          // Ensure templateData has phases property (might be empty or undefined)
          templateData.phases = templateData.phases || [];
          
          // Ensure phases and steps have order fields
          const phases = Array.isArray(templateData.phases) ? templateData.phases.map((phase, index) => ({
            ...phase,
            order: phase.order || index + 1,
            steps: Array.isArray(phase.steps) ? phase.steps.map((step, stepIndex) => ({
              ...step,
              order: step.order || stepIndex + 1
            })) : []
          })) : [];
          
          setFormData({
            name: templateData.name || '',
            description: templateData.description || '',
            phases
          });
          
          // Template data loaded successfully
          console.log('[TemplateForm] Template data loaded successfully');
        } catch (err) {
          console.error('[TemplateForm] Error fetching template:', err);
          setError('Failed to load template. The template may not exist or the server is unavailable.');
        } finally {
          setLoading(false);
        }
      };
      
      fetchTemplate();
    } else if (!isEditing && id === undefined) {
      // Initialize with empty template structure
      console.log('[TemplateForm] Initializing empty template structure');
      setFormData({
        name: '',
        description: '',
        phases: []
      });
    }
  }, [id, isEditing]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Sort phases and steps by order
      const sortedPhases = [...formData.phases].map(phase => ({
        ...phase,
        steps: Array.isArray(phase.steps) 
          ? [...phase.steps].sort((a, b) => a.order - b.order)
          : [] // Ensure steps is always an array
      })).sort((a, b) => a.order - b.order);
      
      // Sanitize phases data - ensure no undefined or circular references
      const sanitizedPhases = sortedPhases.map(phase => ({
        ...(phase.id ? { id: phase.id } : {}),
        name: phase.name || '',
        description: phase.description || '',
        order: phase.order || 1,
        video_url: phase.video_url || '',
        steps: (phase.steps || []).map(step => ({
          ...(step.id ? { id: step.id } : {}),
          name: step.name || '',
          description: step.description || '',
          order: step.order || 1,
          status: step.status || 'Not Started',
          video_url: step.video_url || ''
        }))
      }));
      
      const payload = {
        name: formData.name,
        description: formData.description || '',
        phases: sanitizedPhases
      };
      
      console.log('Submitting template with data:', JSON.stringify(payload, null, 2));
      
      try {
        let response;
        if (isEditing) {
          console.log(`Sending PUT request to /projects/templates/${id} with IDs preserved`);
          response = await api.put(`/projects/templates/${id}`, payload);
          console.log('Template updated successfully:', JSON.stringify(response.data, null, 2));
          navigate('/projects');
        } else {
          console.log('Sending POST request to /projects/templates');
          response = await api.post('/projects/templates', payload);
          console.log('Template created successfully:', JSON.stringify(response.data, null, 2));
          
          // Redirect to the edit page for the new template, so users can add RACI assignments
          if (response.data && response.data.id) {
            navigate(`/projects/templates/${response.data.id}`);
            toast.success('Template created! You can now add RACI assignments.');
          } else {
            navigate('/projects');
          }
        }
      } catch (apiError) {
        console.error('API error when saving template:', apiError);
        if (apiError.response) {
          console.error('Error response:', apiError.response.data);
          setError(`Failed to save template: ${apiError.response.data.error || apiError.response.data.details || 'Unknown error'}`);
        } else if (apiError.request) {
          console.error('No response received:', apiError.request);
          setError('Failed to save template: No response received from server. Please check your connection.');
        } else {
          setError(`Failed to save template: ${apiError.message}`);
        }
      }
    } catch (err) {
      console.error('Error preparing template data:', err);
      setError(`Failed to prepare template data: ${err.message}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };
  
  // Phase management
  const addPhase = () => {
    setFormData(prev => ({
      ...prev,
      phases: [
        ...prev.phases,
        {
          name: '',
          description: '',
          order: prev.phases.length + 1,
          steps: []
        }
      ]
    }));
  };
  
  const removePhase = (index) => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.filter((_, i) => i !== index)
    }));
  };
  
  const updatePhase = (index, phaseData) => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.map((phase, i) => (i === index ? { ...phase, ...phaseData } : phase))
    }));
  };
  
  // Step management
  const addStep = (phaseIndex) => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.map((phase, i) => {
        if (i === phaseIndex) {
          return {
            ...phase,
            steps: [
              ...phase.steps,
              {
                name: '',
                description: '',
                order: phase.steps.length + 1
              }
            ]
          };
        }
        return phase;
      })
    }));
  };
  
  const removeStep = (phaseIndex, stepIndex) => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.map((phase, i) => {
        if (i === phaseIndex) {
          return {
            ...phase,
            steps: phase.steps.filter((_, j) => j !== stepIndex)
          };
        }
        return phase;
      })
    }));
  };
  
  const updateStep = (phaseIndex, stepIndex, stepData) => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.map((phase, i) => {
        if (i === phaseIndex) {
          return {
            ...phase,
            steps: phase.steps.map((step, j) => {
              if (j === stepIndex) {
                return { ...step, ...stepData };
              }
              return step;
            })
          };
        }
        return phase;
      })
    }));
  };
  
  // Helper to move steps and phases up and down
  const movePhase = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= formData.phases.length) return;
    
    const updatedPhases = [...formData.phases];
    const temp = updatedPhases[index];
    updatedPhases[index] = updatedPhases[newIndex];
    updatedPhases[newIndex] = temp;
    
    // Update order values
    updatedPhases.forEach((phase, i) => {
      phase.order = i + 1;
    });
    
    setFormData(prev => ({ ...prev, phases: updatedPhases }));
  };
  
  const moveStep = (phaseIndex, stepIndex, direction) => {
    const phase = formData.phases[phaseIndex];
    const newStepIndex = stepIndex + direction;
    if (newStepIndex < 0 || newStepIndex >= phase.steps.length) return;
    
    const updatedPhases = [...formData.phases];
    const updatedSteps = [...updatedPhases[phaseIndex].steps];
    const temp = updatedSteps[stepIndex];
    updatedSteps[stepIndex] = updatedSteps[newStepIndex];
    updatedSteps[newStepIndex] = temp;
    
    // Update order values
    updatedSteps.forEach((step, i) => {
      step.order = i + 1;
    });
    
    updatedPhases[phaseIndex] = { ...phase, steps: updatedSteps };
    setFormData(prev => ({ ...prev, phases: updatedPhases }));
  };
  
  // Handle rendering template RACI assignments with proper messages
  const renderTemplateRaci = (entityType, entityObj) => {
    console.log(`[renderTemplateRaci] Called for ${entityType}, entity:`, entityObj);
    
    // For brand new entities that don't have an ID yet
    if (!entityObj.id) {
      console.log(`[renderTemplateRaci] No ID found for ${entityType}, showing placeholder`);
      return (
        <div className="bg-white shadow rounded-lg p-4">
          <p className="text-gray-500">RACI assignments will be available after saving this {entityType}.</p>
        </div>
      );
    }
    
    console.log(`[renderTemplateRaci] Rendering RACI for ${entityType}:${entityObj.id}`);
    
    // For entities with an ID
    return (
      <RaciErrorBoundary>
        <SafeRaciContainer entityType={entityType} entityId={entityObj.id} />
      </RaciErrorBoundary>
    );
  };
  
  if (loading && isEditing) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="w-12 h-12 border-t-2 border-b-2 border-indigo-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading template...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">
          {isEditing ? 'Edit Template' : 'Create Template'}
        </h1>
        <Link
          to="/projects"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </Link>
      </div>
      
      {error && (
        <div className="p-4 text-red-700 bg-red-100 rounded-md">
          <p>{error}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white shadow-md border border-gray-200 overflow-hidden sm:rounded-lg">
          <div className="px-6 py-6 sm:p-6">
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md h-10"
                />
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                  className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Project-level assignments */}
        {id && (
          <div className="bg-white shadow-md border border-gray-200 overflow-hidden sm:rounded-lg">
            <div className="px-6 py-5 border-b border-gray-200 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Project Assignments</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Define default assignments at the project level
              </p>
            </div>
            <div className="px-6 py-5 sm:p-6">
              {renderTemplateRaci('project', { id })}
            </div>
          </div>
        )}
        
        {/* Phases Section */}
        <div className="bg-white shadow-md border border-gray-200 overflow-hidden sm:rounded-lg">
          <div className="px-6 py-5 border-b border-gray-200 sm:px-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Phases</h3>
              <button
                type="button"
                onClick={addPhase}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Add Phase
              </button>
            </div>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Define the phases of your project template
            </p>
          </div>
          
          <div className="px-6 py-6 sm:p-6">
            <div className="space-y-8">
              {formData.phases.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-gray-500">No phases added yet. Click "Add Phase" to create one.</p>
                </div>
              ) : (
                formData.phases.map((phase, phaseIndex) => (
                  <div key={phaseIndex} className="bg-white p-6 rounded-lg shadow border-l-4 border-indigo-500 border-t border-r border-b border-gray-300">
                    <div className="flex items-center mb-4">
                      <span className="flex items-center justify-center bg-indigo-600 text-white rounded-full h-8 w-8 font-semibold mr-3">
                        {phaseIndex + 1}
                      </span>
                      <h4 className="text-lg font-medium text-gray-900">Phase {phaseIndex + 1}</h4>
                    </div>
                    
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-6">
                          <div className="sm:col-span-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Phase Name
                            </label>
                            <input
                              type="text"
                              value={phase.name}
                              onChange={(e) => updatePhase(phaseIndex, { name: e.target.value })}
                              required
                              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md h-10"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Order
                            </label>
                            <input
                              type="number"
                              value={phase.order}
                              onChange={(e) => updatePhase(phaseIndex, { order: parseInt(e.target.value) || 0 })}
                              min="1"
                              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md h-10"
                            />
                          </div>
                        </div>
                        
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            value={phase.description || ''}
                            onChange={(e) => updatePhase(phaseIndex, { description: e.target.value })}
                            rows={3}
                            className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>

                        {/* Add video URL field for phases */}
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Instruction Video URL
                          </label>
                          <div className="mt-1 flex rounded-md shadow-sm">
                            <input
                              type="text"
                              value={phase.video_url || ''}
                              onChange={(e) => updatePhase(phaseIndex, { video_url: e.target.value })}
                              placeholder="YouTube or Vimeo URL"
                              className="flex-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md h-10"
                            />
                            {phase.video_url && (
                              <button
                                type="button"
                                onClick={() => window.open(phase.video_url, '_blank')}
                                className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                </svg>
                                Test
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="ml-6 flex-shrink-0 flex flex-col space-y-3">
                        <button
                          type="button"
                          onClick={() => movePhase(phaseIndex, -1)}
                          disabled={phaseIndex === 0}
                          className={`inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white ${
                            phaseIndex === 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                          } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                          title="Move phase up"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => movePhase(phaseIndex, 1)}
                          disabled={phaseIndex === formData.phases.length - 1}
                          className={`inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white ${
                            phaseIndex === formData.phases.length - 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                          } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                          title="Move phase down"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => removePhase(phaseIndex)}
                          className="inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          title="Remove phase"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    
                    {/* Phase Assignments */}
                    {phase.id && id && (
                      <div className="mt-6 pt-4 border-t border-gray-300">
                        <h5 className="text-sm font-medium text-gray-700 mb-3">Phase Assignments</h5>
                        {renderTemplateRaci('phase', phase)}
                      </div>
                    )}
                    {!phase.id && (
                      <div className="mt-6 pt-4 border-t border-gray-300">
                        <div className="bg-yellow-50 p-3 rounded text-sm text-yellow-700 border border-yellow-200">
                          Save the template to enable RACI assignments for this phase.
                        </div>
                      </div>
                    )}
                    
                    {/* Steps for this phase */}
                    <div className="mt-6 pt-4 border-t border-gray-300">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-medium text-gray-800 flex items-center">
                          <span className="mr-2">Steps for Phase {phaseIndex + 1}</span>
                        </h4>
                        <button
                          type="button"
                          onClick={() => addStep(phaseIndex)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Step
                        </button>
                      </div>
                      
                      {phase.steps.length === 0 ? (
                        <div className="text-center py-6 mt-2 bg-white rounded-lg border-2 border-dashed border-gray-300">
                          <p className="text-gray-500">No steps added yet. Click "Add Step" to create one.</p>
                        </div>
                      ) : (
                        <div className="mt-3 space-y-5">
                          {phase.steps.map((step, stepIndex) => (
                            <div 
                              key={`step-assignments-${stepIndex}`} 
                              className="mt-3 rounded-lg border-l-4 border-indigo-400 bg-white shadow"
                            >
                              <div className="p-4 flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center mb-3">
                                    <span className="flex items-center justify-center bg-indigo-100 text-indigo-800 rounded-full h-6 w-6 text-xs font-medium mr-2">
                                      {stepIndex + 1}
                                    </span>
                                    <h5 className="font-medium text-gray-800">Step {stepIndex + 1}</h5>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
                                    <div className="sm:col-span-4">
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Step Name
                                      </label>
                                      <input
                                        type="text"
                                        value={step.name}
                                        onChange={(e) => updateStep(phaseIndex, stepIndex, { name: e.target.value })}
                                        required
                                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md h-9"
                                      />
                                    </div>
                                    <div className="sm:col-span-2">
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Order
                                      </label>
                                      <input
                                        type="number"
                                        value={step.order}
                                        onChange={(e) => updateStep(phaseIndex, stepIndex, { order: parseInt(e.target.value) || 0 })}
                                        min="1"
                                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md h-9"
                                      />
                                    </div>
                                  </div>
                                  
                                  <div className="mt-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Description
                                    </label>
                                    <textarea
                                      value={step.description || ''}
                                      onChange={(e) => updateStep(phaseIndex, stepIndex, { description: e.target.value })}
                                      rows={2}
                                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                    />
                                  </div>

                                  {/* Add video URL field for steps */}
                                  <div className="mt-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Instruction Video URL
                                    </label>
                                    <div className="mt-1 flex rounded-md shadow-sm">
                                      <input
                                        type="text"
                                        value={step.video_url || ''}
                                        onChange={(e) => updateStep(phaseIndex, stepIndex, { video_url: e.target.value })}
                                        placeholder="YouTube or Vimeo URL"
                                        className="flex-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md h-9"
                                      />
                                      {step.video_url && (
                                        <button
                                          type="button"
                                          onClick={() => window.open(step.video_url, '_blank')}
                                          className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                        >
                                          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                          </svg>
                                          Test
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="ml-4 flex-shrink-0 flex flex-col space-y-2">
                                  <button
                                    type="button"
                                    onClick={() => moveStep(phaseIndex, stepIndex, -1)}
                                    disabled={stepIndex === 0}
                                    className={`inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white ${
                                      stepIndex === 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                                    } focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-indigo-500`}
                                    title="Move step up"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => moveStep(phaseIndex, stepIndex, 1)}
                                    disabled={stepIndex === phase.steps.length - 1}
                                    className={`inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white ${
                                      stepIndex === phase.steps.length - 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                                    } focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-indigo-500`}
                                    title="Move step down"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeStep(phaseIndex, stepIndex)}
                                    className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-red-500"
                                    title="Remove step"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                              {/* Step RACI Assignments */}
                              <div className="bg-gray-50 p-4 border-t border-gray-200 rounded-b-lg">
                                <h6 className="text-sm font-medium text-gray-700 mb-2">Step Assignments</h6>
                                {step.id && id ? (
                                  <>
                                    {renderTemplateRaci('step', step)}
                                  </>
                                ) : (
                                  <div className="bg-yellow-50 p-3 rounded text-sm text-yellow-700 border border-yellow-200">
                                    Save the template to enable RACI assignments for this step.
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Bottom Add Step Button for better UX - Moved outside and made more prominent */}
                      <div className="mt-6 bg-gray-50 p-4 rounded-md border border-gray-200 flex justify-center">
                        <button
                          type="button"
                          onClick={() => addStep(phaseIndex)}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          {phase.steps.length > 0 ? 'Add Another Step' : 'Add First Step'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          
          {/* Bottom Add Phase Button for better UX */}
          {formData.phases.length > 0 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-center">
              <button
                type="button"
                onClick={addPhase}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Another Phase
              </button>
            </div>
          )}
        </div>
        
        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              'Save Template'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TemplateForm;